import { DecimalPipe, PercentPipe } from '@angular/common';

export enum FipeComparisonMode {
  ATTAINMENT = 'ATTAINMENT',
  DIFFERENCE = 'DIFFERENCE',
}

export interface FipeComparisonViewModel {
  text: string;
  suffix: string;
  isAboveFipe: boolean;
}

export type FipeComparisonDigits = '1.0-0' | '1.1-1';
export type FipeComparisonModeInput = FipeComparisonMode | null | undefined;

const decimalPipe = new DecimalPipe('pt-BR');
const percentPipe = new PercentPipe('pt-BR');

export function resolveFipeComparisonMode(
  mode?: FipeComparisonModeInput,
): FipeComparisonMode {
  return mode === FipeComparisonMode.DIFFERENCE
    ? FipeComparisonMode.DIFFERENCE
    : FipeComparisonMode.ATTAINMENT;
}

function isValidPrice(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function formatAttainment(attainmentPercent: number, digits: FipeComparisonDigits): string {
  return `${decimalPipe.transform(attainmentPercent, digits)}%`;
}

function formatDifference(diffRatio: number, digits: FipeComparisonDigits): string {
  const percentDigits = digits === '1.1-1' ? '1.1-1' : '1.0-0';
  return percentPipe.transform(diffRatio, percentDigits) ?? '';
}

export function buildFipeComparison(
  price: number | null | undefined,
  fipePrice: number | null | undefined,
  mode?: FipeComparisonModeInput,
  digits: FipeComparisonDigits = '1.0-0',
): FipeComparisonViewModel | null {
  if (!isValidPrice(fipePrice) || fipePrice <= 0 || !isValidPrice(price)) {
    return null;
  }

  const resolvedMode = resolveFipeComparisonMode(mode);
  const diffRatio = price / fipePrice - 1;

  if (resolvedMode === FipeComparisonMode.ATTAINMENT) {
    const attainmentPercent = (price / fipePrice) * 100;
    return {
      text: formatAttainment(attainmentPercent, digits),
      suffix: 'da FIPE',
      isAboveFipe: attainmentPercent > 100,
    };
  }

  return {
    text: formatDifference(diffRatio, digits),
    suffix: 'da FIPE',
    isAboveFipe: diffRatio > 0,
  };
}

export function buildFipeComparisonFromDiff(
  fipeDiff: number | null | undefined,
  mode?: FipeComparisonModeInput,
  digits: FipeComparisonDigits = '1.0-0',
): FipeComparisonViewModel | null {
  if (!isValidPrice(fipeDiff)) {
    return null;
  }

  const resolvedMode = resolveFipeComparisonMode(mode);

  if (resolvedMode === FipeComparisonMode.ATTAINMENT) {
    const attainmentPercent = (1 + fipeDiff) * 100;
    return {
      text: formatAttainment(attainmentPercent, digits),
      suffix: 'da FIPE',
      isAboveFipe: attainmentPercent > 100,
    };
  }

  return {
    text: formatDifference(fipeDiff, digits),
    suffix: 'da FIPE',
    isAboveFipe: fipeDiff > 0,
  };
}

export function getFipeComparisonColor(
  isAboveFipe: boolean,
  accentColor = '#16B097',
): string {
  return isAboveFipe ? '#db360c' : accentColor;
}

export function isFipeOutOfExpectedRange(
  price: number | string | null | undefined,
  fipePrice: number | string | null | undefined,
): boolean {
  const numericPrice = toNumericPrice(price);
  const numericFipe = toNumericPrice(fipePrice);

  if (numericFipe == null || numericFipe <= 0 || numericPrice == null || numericPrice <= 0) {
    return false;
  }

  const ratio = numericPrice / numericFipe;
  return ratio > 1 || ratio < 0.5;
}

function toNumericPrice(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const normalized = value.includes(',')
      ? value.replace(/\./g, '').replace(',', '.')
      : value;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export const FIPE_COMPARISON_DISPLAY_MODE_OPTIONS: {
  value: FipeComparisonMode;
  label: string;
}[] = [
  {
    value: FipeComparisonMode.ATTAINMENT,
    label: 'Atingimento (ex.: 75% da FIPE)',
  },
  {
    value: FipeComparisonMode.DIFFERENCE,
    label: 'Diferença (ex.: -25% da FIPE)',
  },
];
